"use client"

import React, {useEffect, useLayoutEffect, useRef} from "react";
import {motion, useAnimationControls, useReducedMotion} from "framer-motion";
import {cn} from "@/lib/utils";
import Image from "next/image";
import gsap from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

import dashedLine from "@/public/leaderboards/leaderboards-dashed-line.svg";
import julian from "@/public/animatedAssets/leaderboards-rank3.jpg";
import bitbullDashed from "@/public/animatedAssets/bitbull-dashed.svg";

type State = "collapsed" | "expanded";

export const AnimatedAsset4 = ({
  className = "",
  language,
}: {
  className?: string;
  language: "en" | "de";
}) => {
  const controls = useAnimationControls();
  const prefersReduced = useReducedMotion();
  const stateRef = useRef<State>("collapsed");
  const rootRef = useRef<HTMLDivElement | null>(null);

  const setState = (next: State) => {
    if (stateRef.current === next) return Promise.resolve();
    stateRef.current = next;
    return controls.start(next);
  };

  useEffect(() => {
    controls.set("collapsed");
    stateRef.current = "collapsed";
  }, [controls]);

  useLayoutEffect(() => {
    if (prefersReduced) return;
    const el = rootRef.current;
    if (!el) return;

    const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

    if (isTouchDevice) {
      const st = ScrollTrigger.create({
        trigger: el,
        start: "top 50%",
        end: "bottom 50%",
        onEnter: () => setState("expanded"),
        onLeave: () => {},
        onEnterBack: () => setState("expanded"),
        onLeaveBack: () => setState("collapsed"),
        invalidateOnRefresh: true,
      });

      return () => st.kill();
    }

    return;
  }, [prefersReduced, controls]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMouseEnter = async () => {
    if (prefersReduced) return;
    await setState("expanded");
  };

  const handleMouseLeave = () => {
    if (prefersReduced) return;
    setState("collapsed");
  };

  const ease = {ease: "circOut", duration: .6};

  const shiftUp = {
    collapsed: {y: 0, transition: ease},
    expanded: {y: "-63rem", transition: ease},
  }

  const gradientTop = {
    collapsed: {y: 0, transition: ease},
    expanded: {y: "-100%", transition: ease},
  }

  const gradientBottom = {
    collapsed: {y: 0, transition: ease},
    expanded: {y: "50%", transition: ease},
  }

  return (
    <div
      ref={rootRef}
      className={cn(
        "w-full h-[50rem] bg-[#0A1A1D] flex flex-col items-start justify-start px-[2rem] overflow-clip pt-[2.5rem]",
        "relative after:absolute after:h-[2px] after:w-full after:left-0 after:bottom-0 after:bg-[#D9F9B9]",
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div
        className="w-[2.125rem] absolute left-[9rem] top-[1rem]"
        variants={shiftUp}
        animate={controls}
        initial="collapsed"
      >
        <Image
          className="!w-full"
          src={dashedLine}
          alt="dashed Line"
        />
      </motion.div>

      <motion.div
        className="h-[25rem] opacity-70 w-full absolute left-0 top-0 bg-gradient-to-b from-[#0A1A1D] pointer-events-none"
        variants={gradientTop}
        animate={controls}
        initial="collapsed"
      />

      <motion.div
        className="flex flex-col gap-[3rem] pl-[2rem] relative"
        variants={shiftUp}
        animate={controls}
        initial="collapsed"
      >
        {Array.from({ length: 15 }, (_, index) => (
          <div
            key={index}
            className="flex items-center relative gap-[9rem] h-[6rem] flex-shrink-0"
          >
            <div className={cn(
              "w-[3rem] h-[3rem] flex items-center justify-center flex-shrink-0 relative",
              (index === 2 || index === 9) ? "text-[#D9F9B9]/80" : "text-white/[.15]"
            )}>
              {index + 1}
            </div>

            {(index === 2 || index === 9) && (
              <div className="flex items-center gap-[2rem] relative">
                <div className={cn(
                  "absolute w-[calc(100%+3rem)] h-[calc(100%+1.5rem)] -left-[.75rem] top-1/2 -translate-y-1/2  rounded-[1rem]",
                  index === 2 ? "bg-white" : "bg-[#D9F9B9]"
                )}>
                  <div className={cn(
                    "absolute h-[3.8rem] w-[1.9rem] top-1/2 -translate-y-1/2 left-px -translate-x-full",
                    "[clip-path:polygon(0_1.9rem,1.9rem_0,100%_0,100%_100%,1.9rem_100%,0_calc(100%-1.9rem))]",
                    index === 2 ? "bg-white" : "bg-[#D9F9B9]"
                  )}/>
                </div>

                <Image
                  className="!w-[6rem] rounded-full flex-shrink-0 relative"
                  src={index === 2 ? julian : bitbullDashed}
                  alt=""
                />
                <p className="mb-0 leading-none flex-shrink-0 text-black text-[2.125rem] relative flex flex-col items-start gap-[.5rem]">
                  {index === 2 ? (
                    <>
                      <span className="font-medium">Julian Budach</span>
                      <span>{language === "en" ? "You are here" : "Du bist hier"}</span>
                    </>
                  ) : (
                    <span className="font-medium">{language === "en" ? "We are here" : "Wir sind hier"}</span>
                  )}
                </p>
              </div>
            )}
          </div>
        ))}
      </motion.div>

      <motion.div
        className="h-[21.125rem] w-full absolute left-0 bottom-0 bg-gradient-to-t from-[#0A1A1D] pointer-events-none"
        variants={gradientBottom}
        animate={controls}
        initial="collapsed"
      />

      {/* Top gradual blur */}
      <div className="absolute top-0 left-0 w-full h-[15rem] pointer-events-none">
        <div className="relative w-full h-full">
          {/* medium */}
          <div style={{
            position: 'absolute',
            inset: 0,
            WebkitBackdropFilter: 'blur(.2rem)',
            backdropFilter: 'blur(.2rem)',
            WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 45%, transparent 95%)',
            maskImage: 'linear-gradient(to bottom, black 0%, black 45%, transparent 95%)'
          }} />
          {/* light */}
          <div style={{
            position: 'absolute',
            inset: 0,
            WebkitBackdropFilter: 'blur(.1rem)',
            backdropFilter: 'blur(.1rem)',
            WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 60%, transparent 100%)',
            maskImage: 'linear-gradient(to bottom, black 0%, black 60%, transparent 100%)'
          }} />
        </div>
      </div>

      {/* Bottom gradual blur */}
      <div className="absolute bottom-0 left-0 w-full h-[10rem] lg:h-[15rem] pointer-events-none">
        <div className="relative w-full h-full">
          {/* light */}
          <div style={{
            position: 'absolute',
            inset: 0,
            WebkitBackdropFilter: 'blur(.2rem)',
            backdropFilter: 'blur(.2rem)',
            WebkitMaskImage: 'linear-gradient(to top, black 0%, black 60%, transparent 100%)',
            maskImage: 'linear-gradient(to top, black 0%, black 60%, transparent 100%)'
          }} />
          {/* medium */}
          <div style={{
            position: 'absolute',
            inset: 0,
            WebkitBackdropFilter: 'blur(.2rem)',
            backdropFilter: 'blur(.2rem)',
            WebkitMaskImage: 'linear-gradient(to top, black 0%, black 45%, transparent 95%)',
            maskImage: 'linear-gradient(to top, black 0%, black 45%, transparent 95%)'
          }} />
        </div>
      </div>
    </div>
  );
};
